import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import { format, formatDistanceToNow } from "date-fns";
import { AgenticAPI } from "@/services/api";
import { useWebSocket } from "@/context/WebSocketContext";
import { WS_CHANNELS } from "@/utils/websocket";
import { formatPercentage } from "@/utils/decorators";
import type {
  WorkflowTask,
  PodInstanceStats,
  PodInstance,
} from "@/services/api/agentic/types";
import {
  KanbanBoard,
  type KanbanColumnData,
  type KanbanCardData,
} from "@/components/kanban";
import { OutputDocuments } from "./OutputDocuments";
import { TickerFeed } from "@/components/scenarios/components/TickerFeed";
import InstanceUpdate from "./InstanceUpdate";
import { TaskChecklist } from "./TaskChecklist";
import { workflowTaskStatusToColumnId } from "@/utils/workflowTaskKanban";

// Define the activity log item type (copied from ActivityLog.tsx)
// Ideally, this would be in a shared types file
export type ActivityLogItem = {
  id: string;
  description?: string;
  timestamp: string;
  worker_id?: string;
  metadata?: Record<string, any>;
  actor?: { name?: string };
  [key: string]: any;
};

interface OverviewProps {
  instanceId: string;
  startDate?: string;
  latestActionDate?: string;
  expectedCompletionDate?: string;
  recentActivities?: ActivityLogItem[];
}

export function Overview({ instanceId, recentActivities }: OverviewProps) {
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [instanceStats, setInstanceStats] = useState<PodInstanceStats | null>(
    null
  );
  const [instanceData, setInstanceData] = useState<PodInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();

  // Initial Kanban Columns Structure (cards will be populated from API)
  const initialKanbanColumns = useMemo(
    (): KanbanColumnData[] => [
      {
        id: "todo",
        title: "To Do",
        color: "#A0AEC0", // Light slate
        cards: [], // Initially empty, populated from tasks
      },
      {
        id: "blocked",
        title: "Blocked",
        color: "#EF4444", // Red
        cards: [], // Initially empty, populated from tasks
      },
      {
        id: "inprogress",
        title: "In Progress",
        color: "#63B3ED", // Blue
        cards: [], // Initially empty, populated from tasks
      },
      {
        id: "done",
        title: "Done",
        color: "#68D391", // Green
        cards: [], // Initially empty, populated from tasks
      },
    ],
    []
  ); // Empty dependency array ensures this is created only once

  const [kanbanColumns, setKanbanColumns] =
    useState<KanbanColumnData[]>(initialKanbanColumns);

  const handleCardMove = (
    cardId: string,
    sourceColumnId: string,
    destinationColumnId: string,
    newIndex: number // Can be used for reordering within a column in the future
  ) => {
    // Here you would typically update your backend or global state
    // For now, the KanbanBoard component handles its internal state update for columns
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [tasksData, statsData, podInstanceData] = await Promise.all([
          AgenticAPI.getAgenticWorkflowTasks(instanceId),
          AgenticAPI.getPodInstanceStats(instanceId),
          AgenticAPI.getPodInstance(instanceId),
        ]);

        const fetchedTasks = Array.isArray(tasksData) ? tasksData : [];
        setTasks(fetchedTasks);
        setInstanceStats(statsData);
        setInstanceData(podInstanceData);

        // Map fetched tasks to Kanban cards
        const taskCards: KanbanCardData[] = fetchedTasks.map((task) => ({
          id: task._id,
          title: task.name,
          content: task.description,
          columnId: workflowTaskStatusToColumnId(task.status.current_status),
          assigneeName: task.assignee?.name,
          status: task?.status,
          // assigneeAvatarUrl: task.assignee?.avatarUrl, // If available
        }));

        // Distribute cards into respective columns
        const newKanbanColumns = initialKanbanColumns.map((column) => ({
          ...column,
          cards: taskCards.filter((card) => card.columnId === column.id),
        }));
        setKanbanColumns(newKanbanColumns);
      } catch (err) {
        console.error("Error fetching overview data:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load overview data"
        );
        setTasks([]);
        setInstanceStats(null);
        setInstanceData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [instanceId]);

  // Effect to update Kanban columns when tasks or loading state change
  useEffect(() => {
    if (loading) return; // Don't update if still loading initial data

    // Map fetched tasks to Kanban cards
    const taskCards: KanbanCardData[] = tasks.map((task) => ({
      id: task._id,
      title: task.name,
      content: task.description,
      columnId: workflowTaskStatusToColumnId(task.status.current_status),
      assigneeName: task.assignee?.name,
      status: task?.status,
      // assigneeAvatarUrl: task.assignee?.avatarUrl, // If available
    }));

    // Distribute cards into respective columns
    const newKanbanColumns = initialKanbanColumns.map((column) => ({
      ...column,
      cards: taskCards.filter((card) => card.columnId === column.id),
    }));
    setKanbanColumns(newKanbanColumns);
  }, [tasks, loading, initialKanbanColumns]);

  // WebSocket Subscription and Message Handling
  useEffect(() => {
    const channel = WS_CHANNELS.TASKS;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe]);

  useEffect(() => {
    const message: any = lastMessage.get(WS_CHANNELS.TASKS);

    if (message && message.full_document) {
      const taskData = message.full_document as WorkflowTask & {
        pod_instance_id?: string;
      };

      // Check if the task belongs to the current instanceId
      if (taskData.pod_instance_id !== instanceId) {
        return;
      }

      if (message.operation === "insert") {
        setTasks((prevTasks) => {
          if (prevTasks.find((task) => task._id === taskData._id)) {
            return prevTasks;
          }
          return [...prevTasks, taskData];
        });
      } else if (message.operation === "update") {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task._id === taskData._id ? { ...task, ...taskData } : task
          )
        );
      }
    }
  }, [lastMessage, instanceId]);

  const formatDate = (
    dateString: string | undefined | null,
    formatStr = "MMM d, yyyy"
  ) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), formatStr);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  return (
    <div className="p-4 w-full text-slate-900 dark:text-slate-100 h-full min-h-0 flex flex-col overflow-hidden">
      {/* Ticker Row - Fixed at top */}
      <div className="text-sm mb-4 shrink-0">
        {recentActivities && recentActivities.length > 0 && (
          <div className="w-full max-w-full text-sm overflow-hidden relative">
            <TickerFeed
              title="Recent Activities"
              items={recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="inline-flex items-baseline gap-2 text-sm"
                >
                  <span className="text-slate-700 dark:text-slate-300">
                    {activity.description ?? "No description"}
                  </span>
                  <span className="text-slate-400 dark:text-slate-400">
                    {formatDistanceToNow(new Date(activity.timestamp), {
                      addSuffix: true,
                    })}
                  </span>
                  {/* Example: You could add a timestamp too */}
                  {/* <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
            ({formatSimpleTimestamp(activity.timestamp)})
          </span> */}
                </div>
              ))}
              scrollSpeed={0.5}
            />
          </div>
        )}
      </div>

      {/* Combined Progress and Dates Widget + Outputs Widget */}
      <div className="flex-1 min-h-0 grid h-full grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="h-full min-h-0">
          <InstanceUpdate instanceData={instanceData} />
        </div>
        <div className="col-span-3 h-full min-h-0 bg-white border border-slate-200/60 dark:bg-slate-800 dark:border-transparent rounded-2xl overflow-hidden">
          <TaskChecklist instanceId={instanceId} />
        </div>
      </div>
    </div>
  );
}
